/**
 * CindraTextUtils - Shared text processing utilities for Cindra components.
 */

/**
 * Extract status messages from LLM-generated content
 *
 * Parses consecutive "I'll" sentences from the beginning of the response
 * and separates them as status messages for special styling.
 *
 * @param content - The full text content from the LLM
 * @returns Object containing optional statusMessage and mainContent
 *
 * @example
 * extractStatusMessage("I'll look up the patient data. I'll analyze their outcomes. Here are the results...")
 * // Returns: { statusMessage: "I'll look up the patient data. I'll analyze their outcomes.", mainContent: "Here are the results..." }
 */
export const extractStatusMessage = (content: string): { statusMessage?: string; mainContent: string } => {
    // Extract all consecutive sentences starting with "I'll" at the beginning of the response
    // Match sentences one by one, collecting those that contain "I'll"
    const sentences: string[] = [];
    let remainingContent = content;

    // Keep matching sentences while they contain "I'll"
    while (remainingContent.trim()) {
        const sentenceMatch = remainingContent.match(/^(.+?[.:!?])\s*/);
        if (!sentenceMatch) break;

        const sentence = sentenceMatch[1];

        // Check if this sentence contains "I'll"
        if (/I'll/i.test(sentence)) {
            sentences.push(sentence);
            remainingContent = remainingContent.substring(sentenceMatch[0].length);
        } else {
            // Stop when we hit a sentence without "I'll"
            break;
        }
    }

    // If we found any "I'll" sentences, separate them as status messages
    if (sentences.length > 0) {
        const statusMessage = sentences.join(' ');
        const mainContent = remainingContent.trim();

        // Only separate if there's significant content after the status messages
        if (mainContent.length > 50) {
            return { statusMessage, mainContent };
        }
    }

    return { mainContent: content };
};

/**
 * Remove status messages from content
 *
 * Removes consecutive "I'll" sentences from the beginning of the content.
 * Useful for cleaning up generated summaries that shouldn't include status messages.
 *
 * @param content - The full text content from the LLM
 * @returns Content with status messages removed
 *
 * @example
 * removeStatusMessages("I'll look up the patient data. I'll analyze their outcomes. Here are the results...")
 * // Returns: "Here are the results..."
 */
export const removeStatusMessages = (content: string): string => {
    const { mainContent } = extractStatusMessage(content);
    return mainContent;
};

/**
 * Remove conversational wrapper text from LLM-generated content
 *
 * Strips common conversational patterns that LLMs add when presenting information,
 * such as "Here is the summary:", "I've generated...", and trailing polite phrases.
 * This is useful for extracting clean content when you want just the core information.
 *
 * @param content - The text content from the LLM
 * @returns Content with conversational wrappers removed
 *
 * @example
 * removeConversationalWrappers("Here is the summary: The patient shows improvement. Let me know if you need anything else.")
 * // Returns: "The patient shows improvement."
 */
export const removeConversationalWrappers = (content: string): string => {
    let cleanedText = content;

    // Common conversational patterns to strip from the beginning
    const conversationalPatterns = [
        /^Here is (?:the )?(?:subjective progress )?summary(?: for patient [^:]+)?:\s*/i,
        /^Here's (?:the )?(?:subjective progress )?summary(?: for patient [^:]+)?:\s*/i,
        /^(?:I've generated|I found) (?:the )?(?:subjective progress )?summary(?: for patient [^:]+)?:\s*/i,
        /^(?:The )?(?:Subjective progress )?(?:summary)(?: for [^:]+)?:\s*/i,
    ];

    // Try to remove conversational prefix
    for (const pattern of conversationalPatterns) {
        cleanedText = cleanedText.replace(pattern, '');
    }

    // Remove any trailing conversational text
    const trailingPatterns = [
        /\n+If you (?:need|have) (?:further|any) (?:details|questions)[^\n]*$/i,
        /\n+Feel free to (?:ask|let me know)[^\n]*$/i,
        /\n+Let me know if you (?:need|have)[^\n]*$/i,
    ];

    for (const pattern of trailingPatterns) {
        cleanedText = cleanedText.replace(pattern, '');
    }

    return cleanedText.trim();
};
